<template>
    <quill-editor
        :id="id"
        ref="editor"
        v-model="html"
        :content="contentHtml"
        :options="editorOption"
        @change="onChangeFormulaHandler"
        :class="[
            'custom-formula',
            { 'is-valid': isValid, 'is-invalid': isInvalid },
        ]"
        :disabled="disabled"
    />
</template>

<script>
import Vue from 'vue'
import VueQuillEditor from 'vue-quill-editor'
import 'quill-mention'

// require styles
import 'quill/dist/quill.snow.css'

import { getGoalsFromAPI, getIndicatorsFromAPI } from './api'
import { mapperToMentions } from '@/views/components/formula'
import { debounce } from 'lodash'
import Swal from 'sweetalert2'
import i18n from '@/i18n'
import { getInitials } from '@/lib/string'
import { hexColorMixin } from '@/mixins'

// mount with global
Vue.use(VueQuillEditor)

export default {
    name: 'Formula',
    mixins: [hexColorMixin],
    props: [
        'id',
        'placeholder',
        'contentHtml',
        'state',
        'disabled',
        'isKpiAvailable',
        'isGoalAvailable',
        'indicatorId',
        'periods',
    ],
    created() {
        this.debouncedSearchGoalHandler = debounce(this.getGoalsFromAPI, 700)
        this.debouncedSearchIndicatorsHandler = debounce(
            this.getIndicatorsFromAPI,
            700
        )
    },
    data() {
        return {
            usersAvatarColors: [],
            html: ``,
            editorOption: {
                placeholder: this.placeholder,
                modules: {
                    toolbar: null,
                    mention: {
                        allowedChars: /^.*$/,
                        mentionDenotationChars: ['@', '#'],
                        source: (searchTerm, renderList, mentionChar) => {
                            if (mentionChar === '#') {
                                this.debouncedSearchIndicatorsHandler(
                                    this.indicatorId,
                                    searchTerm,
                                    renderList
                                )
                            } else {
                                this.debouncedSearchGoalHandler(
                                    this.indicatorId,
                                    searchTerm,
                                    renderList
                                )
                            }
                        },
                        fixMentionsToQuill: true,
                        renderItem: item => {
                            return this.getOptionsMatchesList(item)
                        },
                    },
                },
            },
        }
    },
    computed: {
        editor() {
            return this.$refs.editor.quill
        },
        isValid() {
            return this.state && this.state !== null
        },
        isInvalid() {
            return !this.state && this.state !== null
        },
    },
    methods: {
        onChangeFormulaHandler() {
            this.$emit('changeValue', this.html, this.getFormula())
        },
        getFormula() {
            // Aux functions
            const hasNodeValue = element => !!element.nodeValue
            const hasDataId = element =>
                !!(element.attributes && element.attributes['data-id'])

            // Formula
            const elements = [
                ...this.$el.getElementsByTagName('p')[0].childNodes,
            ]
            const arr = elements.map(element => {
                if (hasNodeValue(element)) {
                    return element.nodeValue
                } else if (hasDataId(element)) {
                    return `[${element.attributes['data-id'].nodeValue}]`
                }
            })
            const string = arr.join('')
            const formula = string.replace(/[^0-9+\-*/()[\].,]/g, '')

            return formula
        },
        renderMatchesList(items, searchTerm, renderList) {
            let matches = []
            items.forEach(item => {
                if (
                    searchTerm.length &&
                    ~item.value.toLowerCase().indexOf(searchTerm.toLowerCase())
                ) {
                    matches.push(item)
                } else if (!searchTerm.length) {
                    matches.push(item)
                }
            })
            renderList(matches, searchTerm)
        },
        getGoalsFromAPI(indicatorId, searchTerm, renderList) {
            if (this.isGoalAvailable) {
                let values = []
                getGoalsFromAPI(indicatorId, searchTerm, this.periods).then(
                    resp => {
                        const {
                            data: {
                                data: { data },
                            },
                        } = resp
                        values = mapperToMentions(data)
                        if (!values.length) {
                            this.noDataFoundAlert(searchTerm)
                            renderList(values, searchTerm)
                        } else {
                            this.renderMatchesList(
                                values,
                                searchTerm,
                                renderList
                            )
                        }
                    }
                )
            } else {
                this.noModuleAlert('noModuleGoal')
            }
        },
        getIndicatorsFromAPI(indicatorId, searchTerm, renderList) {
            if (this.isKpiAvailable) {
                let values = []
                getIndicatorsFromAPI(
                    indicatorId,
                    searchTerm,
                    this.periods
                ).then(resp => {
                    const {
                        data: {
                            data: { data },
                        },
                    } = resp
                    values = mapperToMentions(data)
                    if (!values.length) {
                        this.noDataFoundAlert(searchTerm)
                        renderList(values, searchTerm)
                    } else {
                        this.renderMatchesList(values, searchTerm, renderList)
                    }
                })
            } else {
                this.noModuleAlert('noModuleKpi')
            }
        },
        noModuleAlert(errorKey) {
            Swal({
                type: 'info',
                title: i18n.t('notification.warning'),
                text: i18n.t(`notification.modules.${errorKey}`),
            })
        },
        noDataFoundAlert(title) {
            if (title) {
                Swal({
                    type: 'info',
                    title: i18n.t('notification.formulaList.searchAgain'),
                    text:
                        i18n.t(
                            'notification.formulaList.noDataWasFoundWithTheTitle'
                        ) + title,
                })
            }
        },
        getOptionsMatchesList(item) {
            let avatarHtml
            if (item.user_picture) {
                avatarHtml = this.getAvatarWithUserPictureHtml(
                    item.user_picture
                )
            } else {
                avatarHtml = this.getAvatarWithUserInitialsHtml(item)
            }

            return `<div class="media">
                        <span class="mr-2" title="${item.user_name}">
                            ${avatarHtml}
                        </span>
                        <div class="media-body">
                            <span title="${item.value}">${item.value}</span><br/>
                            <small title="${item.period_name}">${item.period_name}</small>
                        </div>
                    </div>`
        },
        getAvatarWithUserPictureHtml(userPicture) {
            return `<div class='vue-avatar--wrapper avatar-custom-formula d-inline-block mt-2'
                        style='background: url("${userPicture}") 0% 0% / 30px'>
                        <span></span>
                    </div>`
        },
        getAvatarWithUserInitialsHtml(item) {
            const avatarBackgroundColor = this.getAvatarBackgroundColor(
                item.user_name.length,
                item.user_id
            )
            const avatarFontColor = this.lightenDarkenColor(
                avatarBackgroundColor
            )
            const avatarInitials = getInitials(item.user_name)
            return `<div 
                        class="vue-avatar--wrapper avatar-custom-formula d-inline-block" 
                        style="background-color: ${avatarBackgroundColor}; color: ${avatarFontColor}"
                    >
                        <span>${avatarInitials}</span>
                    </div>`
        },
        getAvatarBackgroundColor(seed, userId) {
            let userAvatarColor = this.usersAvatarColors.find(
                (value, index) => index == userId
            )

            if (!userAvatarColor) {
                userAvatarColor = this.randomBackgroundColor(seed)
                this.usersAvatarColors[userId] = userAvatarColor
            }

            return userAvatarColor
        },
    },
}
</script>

<style lang="scss" scope>
@import '@/scss/variables.scss';

.avatar-custom-formula {
    align-items: center;
    border-radius: 50%;
    display: flex;
    font: 12px / 30px Helvetica, Arial, sans-serif;
    font-weight: bold;
    height: 30px;
    justify-content: center;
    text-align: center;
    width: 30px;
}

.custom-formula {
    .ql-mention-list-container {
        width: auto;
        border: 1px auto #f0f0f0;
        border-radius: 4px;
        background-color: #ffffff;
        box-shadow: 0 2px 12px 0 rgba(30, 30, 30, 0.08);
        z-index: 9001;
        max-height: 230px;
        overflow: auto;
    }

    .ql-mention-list {
        list-style: none;
        margin: 0;
        padding: 0;
        overflow: hidden;
    }

    .ql-mention-list-item {
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
        height: 44px;
        inline-size: max-content;
        min-height: 44px;
        overflow: hidden;
        padding: 0 20px;
        text-align: justify;
        width: auto;
    }

    .ql-mention-list-item.selected::after {
        overflow: hidden;
    }

    .ql-mention-list-item.selected {
        background-color: #dce1e6;
        text-decoration: none;
        border-radius: 4px;
        width: auto;
    }

    .mention {
        height: 24px;
        width: 65px;
        border-radius: 6px;
        background-color: #dce1e6;
        padding: 1px;
        margin-right: 2px;
        user-select: all;
    }

    .mention > span {
        margin: 0 3px;
        padding: 1px;
    }

    &.is-valid {
        .ql-container.ql-snow {
            border-color: $isValidColor !important;
            border-radius: 4px;
        }
    }
    &.is-invalid {
        .ql-container.ql-snow {
            border-color: $isInvalidColor !important;
            border-radius: 4px;
        }
    }
}
</style>
